import styled, { css, useTheme } from "styled-components";
import {
  FC,
  forwardRef,
  MouseEvent,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Identifier } from "dnd-core";
import { useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";

import { ActionsWrap, Td, Thumbnail } from "@/components/Table";
import Text from "@/components/Text";
import { ReactComponent as RocketIcon } from "@/assets/icons/rocket.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { ReactComponent as EyeIcon } from "@/assets/icons/eye.svg";
import { ReactComponent as ClosedEyeIcon } from "@/assets/icons/closed-eye.svg";
import { ReactComponent as WinnerIcon } from "@/assets/icons/winner.svg";
import { ReactComponent as NotWinnerIcon } from "@/assets/icons/not-winner.svg";
import { ReactComponent as DuplicateIcon } from "@/assets/icons/duplicate.svg";
import { hexToRgba } from "@/utils/theme";
import {
  OptimisationRetentionResource,
  OptimisationTestsGroupResource,
  OptimisationTestUnitStatsResource,
} from "@/types/resources";
import RetentionGraph from "./RetentionGraph";
import OptimisationStatusBadge from "./OptimisationStatusBadge";
import {
  OptimisationTestStatusEnum,
  TestingCurrencies,
  OptimisationTestTypeEnum,
  GaNetworkType,
  YoutubeTestsViewsThreshold,
} from "@/utils/enums";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { durationFormatter } from "@/utils/helpers";
import { HiddeableTest } from "./TipsTable";
import TargetAudienceModal from "./TargetAudienceModal";
import RetentionGraphPlaceholder from "./RetentionGraphPlaceholder";
import { RootState } from "@/utils/store";
import ScalableThumbnail from "@/components/ScalableThumbnail";
import { GaNetworkTypeContext } from "../index";
import TestCompetitionBadge from "./TestCompetitionBadge";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);

const StyledText = styled(Text)`
  margin-top: 4px;
`;

const StyledOptimisationStatusBadge = styled(OptimisationStatusBadge)`
  margin-bottom: 4px;
`;

const TdInner = styled.div`
  display: flex;
  align-items: flex-start;
  position: relative;
`;

const TextWraper = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 140px;
`;

const StyledTd = styled(Td)<{ $isWinner: boolean }>`
  padding-top: 15px !important;
  padding-bottom: 12px !important;
  ${({ $isWinner, theme }) =>
    $isWinner &&
    css`
      border-left: 3px solid ${theme.colors.success} !important;
    `}

  ${Thumbnail} {
    flex-shrink: 0;
    width: 92px;
    margin-right: 12px;
  }
`;

const Winning = styled(Text)`
  padding: 3px 8px;
  display: inline-flex;
  align-items: center;
  border-radius: 20px;
  background-color: ${({ theme }) => theme.colors.purpleLighter};
  svg {
    margin-right: 4px;
  }
`;

const Author = styled(Text)`
  margin-top: 2px;
`;

const DuplicateText = styled(Text)`
  font-size: 11px;
  svg {
    margin-top: 2px;
  }
  + ${Author} {
    margin: 0;
  }
`;

interface TrProps {
  $duplicate: boolean;
  $isDragging?: boolean;
  $isDraggable?: boolean;
  $isHidden?: boolean;
  $notEnoughViews?: boolean;
  $isMultitest?: boolean;
  $isLastRow: boolean;
}

const Tr = styled.tr<TrProps>`
  ${({ $isDragging, theme }) =>
    $isDragging &&
    css`
      > td {
        background: ${hexToRgba(theme.colors.purpleLight, 0.05)};
      }
    `}
  ${({ $isDraggable }) =>
    $isDraggable &&
    css`
      cursor: move;
    `}
  ${({ $isHidden = false, theme }) =>
    $isHidden &&
    css`
      > td {
        background: #fcfcfc;
      }
      * {
        color: ${theme.colors.gray};
      }
    `}
    ${({ theme, $notEnoughViews = false }) =>
    $notEnoughViews &&
    css`
      > td {
        background: #f3f3f3;
        border-bottom-color: ${theme.colors.grayscaleLight};
      }
    `}
    ${({ theme, $isMultitest = false }) =>
    $isMultitest &&
    css`
      > td {
        background: #fafafa;
        border-bottom-color: ${theme.colors.grayscaleLight};
      }
    `}
    ${({ theme, $isLastRow }) =>
    $isLastRow &&
    css`
      > td {
        background: #fafafa;
        border-bottom-color: ${theme.colors.gray};
        border-bottom-width: 3px;
      }
    `}
`;

const DeleteButton = styled.button`
  color: ${({ theme }) => theme.colors.gray};
  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const VisibilityButton = styled.button`
  color: ${({ theme }) => theme.colors.gray};
  &:hover {
    * {
      color: ${({ theme }) => theme.colors.black};
    }
  }
`;

const LastTd = styled(Td)`
  vertical-align: middle !important;
  position: relative;
  z-index: 2;
`;

const DropText = styled.div`
  margin-bottom: 2px;
  padding-left: 8px;
  font-size: 11px;
  line-height: 15px;
  &:last-child {
    margin-bottom: 0;
  }
  span {
    color: ${({ theme }) => theme.colors.gray};
  }
  position: relative;
  &:after {
    content: "";
    position: absolute;
    width: 4px;
    height: 4px;
    left: 0;
    top: calc(50% - 1px);
    background-color: red;
    border-radius: 50%;
  }
`;

const StatusIndicator = styled.div<{ $status: number }>`
  position: absolute;
  width: 6px;
  height: 6px;
  left: -15px;
  top: calc(50% - 3px);
  border-radius: 50%;
  background: ${({ theme, $status }) => {
    switch ($status) {
      case OptimisationTestStatusEnum.PROCESSING:
        return theme.colors.warning;
      case OptimisationTestStatusEnum.WAITING_FOR_DATA:
        return theme.colors.info;
      case -100:
        return theme.colors.error;
      default:
        return "none";
    }
  }};
`;

const GraphTd = styled(Td)`
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  min-width: 142px;
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 150px;
`;

const TestingAudiencesButton = styled(Text)`
  font-weight: 700;
  &:hover {
    text-decoration: underline;
  }
`;

export const Dot = styled.span<{ $isGray?: boolean }>`
  display: inline-block;
  width: 4px;
  height: 4px;
  margin-bottom: 1px;
  border-radius: 50%;
  border: 1px solid transparent;
  margin-right: 8px;
  background: ${({ theme }) => theme.colors.black};
  ${({ theme, $isGray }) =>
    $isGray &&
    css`
      background: transparent;
      border-color: ${theme.colors.gray};
    `};
`;

const ThumbHolder = styled.div`
  margin-right: 12px;
`;

const BadgesHolder = styled.div`
  display: flex;
  margin-top: 5px;
  gap: 8px;
`;

const MultitestButton = styled.button`
  margin-top: 8px;
  justify-content: center;
  display: flex;
  width: 100%;
  align-items: center;
  border-radius: 8px;
  height: 22px;
  background: ${({ theme }) => theme.colors.lightGray};
  ${Text} {
    margin-left: 5px;
    margin-right: 8px;
  }
`;

const DisapprovedMutitestRowBadge = styled.svg`
  margin-top: 6px;
`;

const MultitestButtonArrow = styled.svg<{ $rotate: boolean }>`
  transition: 0.2s ease-out transform;
  ${({ $rotate }) =>
    $rotate &&
    css`
      transform: rotate(180deg);
    `}
`;

interface Props {
  test: HiddeableTest;
  winning: boolean;
  isDragging?: boolean;
  handlerId?: Identifier | null;
  isDraggable?: boolean;
  groupId?: number;
  hideThumbnail?: boolean;
  areThumbsEnlarged: boolean;
  isMultitestRow?: boolean;
  isLastRow?: boolean;
  setAreThumbsEnlarged: React.Dispatch<React.SetStateAction<boolean>>;
}

const TestTableRow = forwardRef<HTMLTableRowElement, Props>(
  (
    {
      test,
      winning,
      isDragging,
      handlerId,
      isDraggable = false,
      hideThumbnail = false,
      isMultitestRow = false,
      isLastRow = false,
      groupId,
      areThumbsEnlarged,
      setAreThumbsEnlarged,
    },
    ref
  ) => {
    const optionsDataKey = () => {
      switch (test.type) {
        case OptimisationTestTypeEnum.THUMBS:
          return "optimisationThumbsTable";
        case OptimisationTestTypeEnum.TIPS:
          return "optimisationTipsTable";
        case OptimisationTestTypeEnum.SEQUENCES:
          return "optimisationSequenceTable";
        case OptimisationTestTypeEnum.INTRO:
          return "optimisationIntrosTable";
        case OptimisationTestTypeEnum.FINALISATION:
        case OptimisationTestTypeEnum.TITLE:
          return "optimisationFinalisationTable";
      }
    };

    const [showMultitest, setShowMultitest] = useState(false);

    const theme = useTheme();

    const { gaNetworkType } = useContext(GaNetworkTypeContext);

    const statsKey =
      gaNetworkType === GaNetworkType.SEARCH ? "searchStats" : "watchStats";

    const alerts = useAlerts();

    const queryClient = useQueryClient();

    const { id: projectId } = useParams();

    const tableViewOptions = useSelector(
      (rootState: RootState) => rootState.options.tableViews[optionsDataKey()]
    );

    const { data: currencyRate } = useQuery(
      [`${TestingCurrencies.KRW}_to_usd_currency_rate`],
      () =>
        axios
          .get(
            `https://api.exchangerate.host/convert?from=${TestingCurrencies.KRW}&to=USD&amount=100`
          )
          .then((response) => response.data?.info?.rate),
      {
        staleTime: 300 * 1000,
      }
    );

    const dropPoints = useMemo(
      () =>
        test.retention?.reduce(
          (
            previousValue: {
              current: OptimisationRetentionResource;
              previous: OptimisationRetentionResource;
            }[],
            currentValue: OptimisationRetentionResource,
            index: number
          ) =>
            currentValue.marked
              ? [
                  ...previousValue,
                  {
                    current: currentValue,
                    previous: test.retention![index - 1],
                  },
                ]
              : previousValue,
          []
        ),
      [test.retention]
    );

    const CheckExistance: FC<
      PropsWithChildren<{
        property: keyof OptimisationTestUnitStatsResource;
      }>
    > = ({ property, children }) => {
      if (!test[statsKey]?.[property] && !test[statsKey]?.[property]) {
        if (property === "finalValue")
          return <StyledText $type="extraSmall">-</StyledText>;

        return <OptimisationStatusBadge big />;
      }
      return <>{children}</>;
    };

    const [showTargetAudience, setShowTargetAudience] = useState(false);

    const renderMetricsColumns = () => {
      return (
        <>
          <Td>
            <CheckExistance property="views">
              <span>
                <Dot />
                {test[statsKey]?.views ?? "-"}
              </span>
            </CheckExistance>
          </Td>
          {tableViewOptions["Video Title"] && (
            <Td>{test.properties.videoTitle ?? "-"}</Td>
          )}
          {test.type === OptimisationTestTypeEnum.TIPS ? (
            <>
              {tableViewOptions["Scene Duration"] && (
                <Td>
                  {test.properties.sceneDuration ? (
                    durationFormatter(test.properties.sceneDuration)
                  ) : (
                    <OptimisationStatusBadge big />
                  )}
                </Td>
              )}
            </>
          ) : (
            <>
              {tableViewOptions["Video Duration"] && (
                <Td>
                  {test.videoDuration ? (
                    durationFormatter(test.videoDuration)
                  ) : (
                    <OptimisationStatusBadge big />
                  )}
                </Td>
              )}
            </>
          )}
          {tableViewOptions["CTR"] && (
            <Td>
              <CheckExistance property="ctrPercentage">
                <span>
                  <Dot />
                  {test[statsKey]?.ctrPercentage?.toString()
                    ? `${test[statsKey]!.ctrPercentage} %`
                    : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["Watchtime (s)"] && (
            <Td>
              <CheckExistance property="avgWatchtime">
                <span>
                  <Dot />
                  {test[statsKey]?.avgWatchtime ? (
                    <>{test[statsKey]!.avgWatchtime} s</>
                  ) : (
                    "-"
                  )}
                </span>
              </CheckExistance>
            </Td>
          )}

          {tableViewOptions["e30s Reach"] && (
            <Td>
              <CheckExistance property="e30s">
                <span>
                  <Dot />
                  {test[statsKey]?.["e30s"] ?? "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["e45s Reach"] && (
            <Td>
              <CheckExistance property="e45s">
                <span>
                  <Dot />
                  {test[statsKey]?.["e45s"] ?? "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["e60s Reach"] && (
            <Td>
              <CheckExistance property="e60s">
                <span>
                  <Dot />
                  {test[statsKey]?.["e60s"] ?? "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["e80s Reach"] && (
            <Td>
              <CheckExistance property="e80s">
                <span>
                  <Dot />
                  {test[statsKey]?.["e80s"] ?? "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["e120s Reach"] && (
            <Td>
              <CheckExistance property="e120s">
                <span>
                  <Dot />
                  {test[statsKey]?.["e120s"] ?? "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["e150s Reach"] && (
            <Td>
              <CheckExistance property="e150s">
                <span>
                  <Dot />
                  {test[statsKey]?.["e150s"] ?? "-"}
                </span>
              </CheckExistance>
            </Td>
          )}

          {tableViewOptions["e4min Reach"] && (
            <Td>
              <CheckExistance property="e240s">
                <span>
                  <Dot />
                  {test[statsKey]?.e240s?.toString()
                    ? `${test[statsKey]?.e240s} %`
                    : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["e8min Reach"] && (
            <Td>
              <CheckExistance property="eMilestoneViewsPercentage">
                <span>
                  <Dot />
                  {test[statsKey]?.eMilestoneViewsPercentage?.toString()
                    ? `${test[statsKey]?.eMilestoneViewsPercentage} %`
                    : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["e15min Reach"] && (
            <Td>
              <CheckExistance property="e15m">
                <span>
                  <Dot />
                  {test[statsKey]?.e15m?.toString()
                    ? `${test[statsKey]?.e15m} %`
                    : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["e20min Reach"] && (
            <Td>
              <CheckExistance property="e20m">
                <span>
                  <Dot />
                  {test[statsKey]?.e20m?.toString()
                    ? `${test[statsKey]?.e20m} %`
                    : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["e30min Reach"] && (
            <Td>
              <CheckExistance property="e30m">
                <span>
                  <Dot />
                  {test[statsKey]?.e30m?.toString()
                    ? `${test[statsKey]?.e30m} %`
                    : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["e60min Reach"] && (
            <Td>
              <CheckExistance property="e60m">
                <span>
                  <Dot />
                  {test[statsKey]?.e60m?.toString()
                    ? `${test[statsKey]?.e60m} %`
                    : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["30s Reach"] && (
            <Td>
              <CheckExistance property="30sViewsPercentage">
                <span>
                  <Dot />
                  {test[statsKey]?.["30sViewsPercentage"] ?? "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["8min Reach"] && (
            <Td>
              <CheckExistance property="milestoneViewsPercentage">
                <span>
                  <Dot />
                  {test[statsKey]?.milestoneViewsPercentage?.toString()
                    ? `${test[statsKey]?.milestoneViewsPercentage} %`
                    : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["25% Reach"] && (
            <Td>
              <CheckExistance property="p25Rate">
                <span>
                  <Dot />
                  {test[statsKey]?.p25Rate?.toFixed(2) ?? "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["50% Reach"] && (
            <Td>
              <CheckExistance property="p50Rate">
                <span>
                  <Dot />
                  {test[statsKey]?.p50Rate?.toFixed(2) ?? "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["75% Reach"] && (
            <Td>
              <CheckExistance property="p75Rate">
                <span>
                  <Dot />
                  {test[statsKey]?.p75Rate?.toFixed(2) ?? "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["100% Reach"] && (
            <Td>
              <CheckExistance property="p100Rate">
                <span>
                  <Dot />
                  {test[statsKey]?.p100Rate?.toFixed(2) ?? "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["Final Value"] && (
            <Td>
              <StyledOptimisationStatusBadge big status={test.status} />
              <div>
                <CheckExistance property="finalValue">
                  <span>
                    <Dot />
                    {test[statsKey]?.finalValue
                      ? `YTV: ${test[statsKey]?.finalValue}`
                      : "-"}
                  </span>
                </CheckExistance>
              </div>
            </Td>
          )}
          {tableViewOptions["eYTV"] && (
            <Td>
              <CheckExistance property="estimatedFinalValue">
                <span>
                  <Dot />
                  {test[statsKey]?.estimatedFinalValue
                    ? test[statsKey]?.estimatedFinalValue
                    : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["eYTV 30s"] && (
            <Td>
              <CheckExistance property="eytv30s">
                <span>
                  <Dot />
                  {test[statsKey]?.eytv30s ? test[statsKey]?.eytv30s : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["eYTV 45s"] && (
            <Td>
              <CheckExistance property="eytv45s">
                <span>
                  <Dot />
                  {test[statsKey]?.eytv45s ? test[statsKey]?.eytv45s : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["eYTV 60s"] && (
            <Td>
              <CheckExistance property="eytv60s">
                <span>
                  <Dot />
                  {test[statsKey]?.eytv60s ? test[statsKey]?.eytv60s : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["eYTV 80s"] && (
            <Td>
              <CheckExistance property="eytv80s">
                <span>
                  <Dot />
                  {test[statsKey]?.eytv80s ? test[statsKey]?.eytv80s : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["eYTV 120s"] && (
            <Td>
              <CheckExistance property="eytv120s">
                <span>
                  <Dot />
                  {test[statsKey]?.eytv120s ? test[statsKey]?.eytv120s : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["eYTV 150s"] && (
            <Td>
              <CheckExistance property="eytv150s">
                <span>
                  <Dot />
                  {test[statsKey]?.eytv150s ? test[statsKey]?.eytv150s : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["eYTV 4min"] && (
            <Td>
              <CheckExistance property="eytv4">
                <span>
                  <Dot />
                  {test[statsKey]?.eytv4 ? `${test[statsKey]?.eytv4}` : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["eYTV 15min"] && (
            <Td>
              <CheckExistance property="eytv15m">
                <span>
                  <Dot />
                  {test[statsKey]?.eytv15m ? `${test[statsKey]?.eytv15m}` : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["eYTV 20min"] && (
            <Td>
              <CheckExistance property="eytv20m">
                <span>
                  <Dot />
                  {test[statsKey]?.eytv20m ? `${test[statsKey]?.eytv20m}` : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["eYTV 30min"] && (
            <Td>
              <CheckExistance property="eytv30m">
                <span>
                  <Dot />
                  {test[statsKey]?.eytv30m ? `${test[statsKey]?.eytv30m}` : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
          {tableViewOptions["eYTV 60min"] && (
            <Td>
              <CheckExistance property="eytv60m">
                <span>
                  <Dot />
                  {test[statsKey]?.eytv60m ? `${test[statsKey]?.eytv60m}` : "-"}
                </span>
              </CheckExistance>
            </Td>
          )}
        </>
      );
    };

    const renderColumns = () => {
      const { properties } = test;
      return (
        <>
          {tableViewOptions["Channel"] && (
            <Td>{properties.testingChannelName ?? "-"}</Td>
          )}
          {tableViewOptions["Audience Channels"] && (
            <Td>
              {properties.targetChannels?.length ? (
                <TestingAudiencesButton
                  as="button"
                  $type="smallMedium"
                  onClick={() => setShowTargetAudience(true)}
                >
                  {properties.targetChannels.length}
                  <AnimatePresence>
                    {showTargetAudience && (
                      <TargetAudienceModal
                        channels={properties.targetChannels}
                        onClose={() => setShowTargetAudience(false)}
                      />
                    )}
                  </AnimatePresence>
                </TestingAudiencesButton>
              ) : (
                "-"
              )}
            </Td>
          )}
          {tableViewOptions["Audience Segment"] && (
            <Td>{properties.audienceSegment ?? "-"}</Td>
          )}
          {tableViewOptions["GA Campaign Status"] && (
            <Td>{test.gaStatus ?? "-"}</Td>
          )}
          {tableViewOptions["Max CPV"] && (
            <Td>
              ₩ {properties.maxCpv}
              {!!currencyRate && !!properties.maxCpv && (
                <>
                  <br />
                  <Text $type="extraSmall" $color="gray" as="span">
                    ${(parseFloat(properties.maxCpv) * currencyRate).toFixed(2)}
                  </Text>
                </>
              )}
            </Td>
          )}
          {tableViewOptions["Campaign Budget"] && (
            <Td>
              ₩ {properties.campaignBudget}
              {!!currencyRate && !!properties.campaignBudget && (
                <>
                  <br />
                  <Text $type="extraSmall" $color="gray" as="span">
                    $
                    {(
                      parseFloat(properties.campaignBudget) * currencyRate
                    ).toFixed(2)}
                  </Text>
                </>
              )}
            </Td>
          )}
        </>
      );
    };

    const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
      try {
        e.stopPropagation();

        if (!isDraggable && !test.is_hidden) {
          const confirmed = await alerts.confirm(
            `Are you sure you want to delete this test?`,
            test.name
          );

          if (!confirmed) return;
        }

        if (isDraggable || test.is_hidden) {
          const updatedGroup = await api.put<
            { optimisation_test_unit_id: number },
            OptimisationTestsGroupResource
          >(`/v1/optimisation-tests-groups/${groupId}/toggle-visibility`, {
            optimisation_test_unit_id: test.id,
            is_hidden: test.is_hidden ? 0 : 1,
          });

          queryClient.setQueryData<OptimisationTestsGroupResource[]>(
            ["tips-tests-groups", { projectId }],
            (groups) => {
              return groups?.map((group) => {
                if (group.id !== updatedGroup.id) return group;

                return updatedGroup;
              });
            }
          );
        } else {
          await api.delete(`/v1/optimisation-test-units/${test.id}`);

          queryClient.invalidateQueries(["optimisation_tests"], {
            exact: false,
          });
        }

        alerts.success(
          `Test was successfully ${
            isDraggable || test.is_hidden ? "updated" : "deleted"
          }`
        );
      } catch (e) {
        alerts.error();
      }
    };

    const toggleWinnerProperty = async (e: MouseEvent<HTMLButtonElement>) => {
      try {
        e.stopPropagation();

        const confirmed = await alerts.confirm(
          `Are you sure you would like to ${
            test.isMarkedAsWinner ? "un" : ""
          }set this test as winner?`,
          test.name
        );

        if (!confirmed) return;

        await api.put(
          `/v1/optimisation-test-units/${test.id}/toggle-winner-flag`
        );

        alerts.success("Test successfully updated", test.name);

        queryClient.invalidateQueries(
          ["optimisation_tests", { id: test.optimisationProjectId.toString() }],
          {
            exact: false,
          }
        );
      } catch (_) {
        alerts.error();
      }
    };

    const renderActions = () => {
      if (
        [
          OptimisationTestTypeEnum.SEQUENCES,
          OptimisationTestTypeEnum.INTRO,
          OptimisationTestTypeEnum.FINALISATION,
        ].includes(test.type)
      ) {
        return (
          <ActionsWrap>
            <StyledActionsDropdown>
              <ActionsDropdownButton as="button" onClick={toggleWinnerProperty}>
                <span>
                  {test.isMarkedAsWinner ? <NotWinnerIcon /> : <WinnerIcon />}
                </span>
                {test.isMarkedAsWinner ? "Unset winner" : "Set as winner"}
              </ActionsDropdownButton>
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </StyledActionsDropdown>
          </ActionsWrap>
        );
      }

      if (isDraggable || test.is_hidden) {
        return (
          <VisibilityButton onClick={onDelete}>
            {test.is_hidden ? <ClosedEyeIcon /> : <EyeIcon />}
          </VisibilityButton>
        );
      }

      return (
        <DeleteButton onClick={onDelete}>
          <TrashIcon />
        </DeleteButton>
      );
    };

    const onMultitestBtnClick = () => {
      setShowMultitest((prev) => !prev);
    };

    const hasDisapprovedAd = useMemo(() => {
      if (test.multitestEnabled && !isMultitestRow) {
        return !!test[
          gaNetworkType === GaNetworkType.SEARCH ? "statsS" : "statsW"
        ]?.find((stats) => stats.isDisapproved);
      }

      return test[statsKey]?.isDisapproved ?? false;
    }, [test, gaNetworkType, statsKey, isMultitestRow]);

    const showPartialBadge = useMemo(() => {
      if (isMultitestRow || !test.multitestEnabled) {
        return false;
      }

      const viewsThreshold =
        test.type === OptimisationTestTypeEnum.THUMBS
          ? YoutubeTestsViewsThreshold.THUMBS
          : YoutubeTestsViewsThreshold.OTHER;

      const hasSuccessfulTest = test[
        gaNetworkType === GaNetworkType.SEARCH ? "statsS" : "statsW"
      ]?.find((stat) => stat.views !== null);

      if (!hasSuccessfulTest) {
        return false;
      }

      const hasFailedViewsTest = test[
        gaNetworkType === GaNetworkType.SEARCH ? "statsS" : "statsW"
      ]?.find((stat) => (stat?.views ?? 0) < viewsThreshold);

      if (hasFailedViewsTest) {
        return true;
      }

      const totalTests =
        test[gaNetworkType === GaNetworkType.SEARCH ? "statsS" : "statsW"]
          ?.length ?? 0;

      if (totalTests === 0 || totalTests === 3) {
        return false;
      }

      return true;
    }, [isMultitestRow, gaNetworkType, test]);

    return (
      <>
        <Tr
          $isLastRow={isLastRow}
          $duplicate={test.isDuplicate}
          $isHidden={!!test.is_hidden}
          $isMultitest={isMultitestRow}
          $notEnoughViews={(test[statsKey]?.views ?? 0) < 50}
          $isDraggable={isDraggable}
          ref={ref}
          $isDragging={isDragging}
          data-handler-id={handlerId}
        >
          <StyledTd $isWinner={test.isMarkedAsWinner}>
            {isMultitestRow ? (
              <>
                {!!test.youtubeVideoUrl && (
                  <a
                    href={test.youtubeVideoUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {test.youtubeVideoUrl}
                  </a>
                )}
                {hasDisapprovedAd && (
                  <div>
                    <DisapprovedMutitestRowBadge
                      width="97"
                      height="18"
                      viewBox="0 0 97 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        width="97"
                        height="18"
                        rx="9"
                        fill={theme.colors["error"]}
                      />
                      <circle
                        cx="13"
                        cy="9"
                        r="5"
                        fill={theme.colors["white"]}
                      />
                      <path
                        d="M12.5312 7.60662V6.8125H13.5312V7.60662H12.5312ZM12.5312 11.3125V8.13603H13.5312V11.3125H12.5312Z"
                        fill={theme.colors["error"]}
                      />
                      <path
                        d="M22.18 12L24.286 5.52H25.6405L27.7465 12H26.77L24.8305 6.087H25.0735L23.1565 12H22.18ZM23.2735 10.5375V9.6555H26.6575V10.5375H23.2735ZM30.4843 12.135C30.0313 12.135 29.6398 12.0225 29.3098 11.7975C28.9828 11.5725 28.7293 11.2665 28.5493 10.8795C28.3723 10.4895 28.2838 10.0515 28.2838 9.5655C28.2838 9.0765 28.3738 8.64 28.5538 8.256C28.7338 7.869 28.9888 7.5645 29.3188 7.3425C29.6518 7.1175 30.0463 7.005 30.5023 7.005C30.9613 7.005 31.3468 7.1175 31.6588 7.3425C31.9738 7.5645 32.2108 7.869 32.3698 8.256C32.5318 8.643 32.6128 9.0795 32.6128 9.5655C32.6128 10.0515 32.5318 10.488 32.3698 10.875C32.2078 11.262 31.9693 11.5695 31.6543 11.7975C31.3393 12.0225 30.9493 12.135 30.4843 12.135ZM30.6148 11.2845C30.9238 11.2845 31.1758 11.211 31.3708 11.064C31.5658 10.917 31.7083 10.7145 31.7983 10.4565C31.8883 10.1985 31.9333 9.9015 31.9333 9.5655C31.9333 9.2295 31.8868 8.9325 31.7938 8.6745C31.7038 8.4165 31.5628 8.2155 31.3708 8.0715C31.1818 7.9275 30.9403 7.8555 30.6463 7.8555C30.3343 7.8555 30.0778 7.932 29.8768 8.085C29.6758 8.238 29.5258 8.4435 29.4268 8.7015C29.3308 8.9595 29.2828 9.2475 29.2828 9.5655C29.2828 9.8865 29.3308 10.1775 29.4268 10.4385C29.5258 10.6965 29.6728 10.902 29.8678 11.055C30.0658 11.208 30.3148 11.2845 30.6148 11.2845ZM31.9333 12V8.5035H31.8253V5.52H32.7703V12H31.9333ZM35.8312 12V5.52H37.8607C37.9207 5.52 38.0317 5.5215 38.1937 5.5245C38.3587 5.5275 38.5162 5.5395 38.6662 5.5605C39.1732 5.6235 39.5992 5.805 39.9442 6.105C40.2922 6.405 40.5547 6.786 40.7317 7.248C40.9087 7.707 40.9972 8.211 40.9972 8.76C40.9972 9.312 40.9087 9.819 40.7317 10.281C40.5547 10.74 40.2922 11.1195 39.9442 11.4195C39.5992 11.7165 39.1732 11.8965 38.6662 11.9595C38.5162 11.9805 38.3587 11.9925 38.1937 11.9955C38.0317 11.9985 37.9207 12 37.8607 12H35.8312ZM36.7942 11.1045H37.8607C37.9627 11.1045 38.0842 11.1015 38.2252 11.0955C38.3662 11.0895 38.4907 11.0775 38.5987 11.0595C38.9287 10.9965 39.1957 10.854 39.3997 10.632C39.6067 10.407 39.7582 10.131 39.8542 9.804C39.9502 9.477 39.9982 9.129 39.9982 8.76C39.9982 8.379 39.9487 8.0265 39.8497 7.7025C39.7507 7.3755 39.5977 7.1025 39.3907 6.8835C39.1867 6.6615 38.9227 6.5205 38.5987 6.4605C38.4907 6.4395 38.3647 6.4275 38.2207 6.4245C38.0797 6.4185 37.9597 6.4155 37.8607 6.4155H36.7942V11.1045ZM42.135 6.3435V5.43H43.0755V6.3435H42.135ZM42.135 12V7.14H43.0755V12H42.135ZM46.2854 12.1305C45.6944 12.1305 45.2129 12 44.8409 11.739C44.4689 11.478 44.2409 11.1105 44.1569 10.6365L45.1199 10.488C45.1799 10.74 45.3179 10.9395 45.5339 11.0865C45.7529 11.2305 46.0244 11.3025 46.3484 11.3025C46.6424 11.3025 46.8719 11.2425 47.0369 11.1225C47.2049 11.0025 47.2889 10.8375 47.2889 10.6275C47.2889 10.5045 47.2589 10.4055 47.1989 10.3305C47.1419 10.2525 47.0204 10.179 46.8344 10.11C46.6484 10.041 46.3649 9.9555 45.9839 9.8535C45.5669 9.7455 45.2354 9.63 44.9894 9.507C44.7464 9.381 44.5724 9.2355 44.4674 9.0705C44.3654 8.9025 44.3144 8.7 44.3144 8.463C44.3144 8.169 44.3924 7.9125 44.5484 7.6935C44.7044 7.4745 44.9234 7.305 45.2054 7.185C45.4904 7.065 45.8234 7.005 46.2044 7.005C46.5764 7.005 46.9079 7.0635 47.1989 7.1805C47.4899 7.2975 47.7254 7.464 47.9054 7.68C48.0854 7.893 48.1934 8.1435 48.2294 8.4315L47.2664 8.607C47.2334 8.373 47.1239 8.1885 46.9379 8.0535C46.7519 7.9185 46.5104 7.845 46.2134 7.833C45.9284 7.821 45.6974 7.869 45.5204 7.977C45.3434 8.082 45.2549 8.2275 45.2549 8.4135C45.2549 8.5215 45.2879 8.613 45.3539 8.688C45.4229 8.763 45.5549 8.835 45.7499 8.904C45.9449 8.973 46.2329 9.0555 46.6139 9.1515C47.0219 9.2565 47.3459 9.3735 47.5859 9.5025C47.8259 9.6285 47.9969 9.78 48.0989 9.957C48.2039 10.131 48.2564 10.3425 48.2564 10.5915C48.2564 11.0715 48.0809 11.448 47.7299 11.721C47.3819 11.994 46.9004 12.1305 46.2854 12.1305ZM50.6246 12.135C50.2646 12.135 49.9631 12.069 49.7201 11.937C49.4771 11.802 49.2926 11.625 49.1666 11.406C49.0436 11.184 48.9821 10.941 48.9821 10.677C48.9821 10.431 49.0256 10.215 49.1126 10.029C49.1996 9.843 49.3286 9.6855 49.4996 9.5565C49.6706 9.4245 49.8806 9.318 50.1296 9.237C50.3456 9.174 50.5901 9.1185 50.8631 9.0705C51.1361 9.0225 51.4226 8.9775 51.7226 8.9355C52.0256 8.8935 52.3256 8.8515 52.6226 8.8095L52.2806 8.9985C52.2866 8.6175 52.2056 8.3355 52.0376 8.1525C51.8726 7.9665 51.5876 7.8735 51.1826 7.8735C50.9276 7.8735 50.6936 7.9335 50.4806 8.0535C50.2676 8.1705 50.1191 8.3655 50.0351 8.6385L49.1576 8.3685C49.2776 7.9515 49.5056 7.62 49.8416 7.374C50.1806 7.128 50.6306 7.005 51.1916 7.005C51.6266 7.005 52.0046 7.08 52.3256 7.23C52.6496 7.377 52.8866 7.611 53.0366 7.932C53.1146 8.091 53.1626 8.259 53.1806 8.436C53.1986 8.613 53.2076 8.8035 53.2076 9.0075V12H52.3751V10.8885L52.5371 11.0325C52.3361 11.4045 52.0796 11.682 51.7676 11.865C51.4586 12.045 51.0776 12.135 50.6246 12.135ZM50.7911 11.3655C51.0581 11.3655 51.2876 11.319 51.4796 11.226C51.6716 11.13 51.8261 11.0085 51.9431 10.8615C52.0601 10.7145 52.1366 10.5615 52.1726 10.4025C52.2236 10.2585 52.2521 10.0965 52.2581 9.9165C52.2671 9.7365 52.2716 9.5925 52.2716 9.4845L52.5776 9.597C52.2806 9.642 52.0106 9.6825 51.7676 9.7185C51.5246 9.7545 51.3041 9.7905 51.1061 9.8265C50.9111 9.8595 50.7371 9.9 50.5841 9.948C50.4551 9.993 50.3396 10.047 50.2376 10.11C50.1386 10.173 50.0591 10.2495 49.9991 10.3395C49.9421 10.4295 49.9136 10.539 49.9136 10.668C49.9136 10.794 49.9451 10.911 50.0081 11.019C50.0711 11.124 50.1671 11.208 50.2961 11.271C50.4251 11.334 50.5901 11.3655 50.7911 11.3655ZM56.6576 12.135C56.1926 12.135 55.8026 12.0225 55.4876 11.7975C55.1726 11.5695 54.9341 11.262 54.7721 10.875C54.6101 10.488 54.5291 10.0515 54.5291 9.5655C54.5291 9.0795 54.6086 8.643 54.7676 8.256C54.9296 7.869 55.1666 7.5645 55.4786 7.3425C55.7936 7.1175 56.1806 7.005 56.6396 7.005C57.0956 7.005 57.4886 7.1175 57.8186 7.3425C58.1516 7.5645 58.4081 7.869 58.5881 8.256C58.7681 8.64 58.8581 9.0765 58.8581 9.5655C58.8581 10.0515 58.7681 10.4895 58.5881 10.8795C58.4111 11.2665 58.1576 11.5725 57.8276 11.7975C57.5006 12.0225 57.1106 12.135 56.6576 12.135ZM54.3716 14.16V7.14H55.2086V10.6365H55.3166V14.16H54.3716ZM56.5271 11.2845C56.8271 11.2845 57.0746 11.208 57.2696 11.055C57.4676 10.902 57.6146 10.6965 57.7106 10.4385C57.8096 10.1775 57.8591 9.8865 57.8591 9.5655C57.8591 9.2475 57.8096 8.9595 57.7106 8.7015C57.6146 8.4435 57.4661 8.238 57.2651 8.085C57.0641 7.932 56.8076 7.8555 56.4956 7.8555C56.2016 7.8555 55.9586 7.9275 55.7666 8.0715C55.5776 8.2155 55.4366 8.4165 55.3436 8.6745C55.2536 8.9325 55.2086 9.2295 55.2086 9.5655C55.2086 9.9015 55.2536 10.1985 55.3436 10.4565C55.4336 10.7145 55.5761 10.917 55.7711 11.064C55.9661 11.211 56.2181 11.2845 56.5271 11.2845ZM62.1332 12.135C61.6682 12.135 61.2782 12.0225 60.9632 11.7975C60.6482 11.5695 60.4097 11.262 60.2477 10.875C60.0857 10.488 60.0047 10.0515 60.0047 9.5655C60.0047 9.0795 60.0842 8.643 60.2432 8.256C60.4052 7.869 60.6422 7.5645 60.9542 7.3425C61.2692 7.1175 61.6562 7.005 62.1152 7.005C62.5712 7.005 62.9642 7.1175 63.2942 7.3425C63.6272 7.5645 63.8837 7.869 64.0637 8.256C64.2437 8.64 64.3337 9.0765 64.3337 9.5655C64.3337 10.0515 64.2437 10.4895 64.0637 10.8795C63.8867 11.2665 63.6332 11.5725 63.3032 11.7975C62.9762 12.0225 62.5862 12.135 62.1332 12.135ZM59.8472 14.16V7.14H60.6842V10.6365H60.7922V14.16H59.8472ZM62.0027 11.2845C62.3027 11.2845 62.5502 11.208 62.7452 11.055C62.9432 10.902 63.0902 10.6965 63.1862 10.4385C63.2852 10.1775 63.3347 9.8865 63.3347 9.5655C63.3347 9.2475 63.2852 8.9595 63.1862 8.7015C63.0902 8.4435 62.9417 8.238 62.7407 8.085C62.5397 7.932 62.2832 7.8555 61.9712 7.8555C61.6772 7.8555 61.4342 7.9275 61.2422 8.0715C61.0532 8.2155 60.9122 8.4165 60.8192 8.6745C60.7292 8.9325 60.6842 9.2295 60.6842 9.5655C60.6842 9.9015 60.7292 10.1985 60.8192 10.4565C60.9092 10.7145 61.0517 10.917 61.2467 11.064C61.4417 11.211 61.6937 11.2845 62.0027 11.2845ZM65.3273 12V7.14H66.1643V8.319L66.0473 8.166C66.1073 8.01 66.1853 7.8675 66.2813 7.7385C66.3773 7.6065 66.4883 7.4985 66.6143 7.4145C66.7373 7.3245 66.8738 7.2555 67.0238 7.2075C67.1768 7.1565 67.3328 7.1265 67.4918 7.1175C67.6508 7.1055 67.8038 7.113 67.9508 7.14V8.022C67.7918 7.98 67.6148 7.968 67.4198 7.986C67.2278 8.004 67.0508 8.0655 66.8888 8.1705C66.7358 8.2695 66.6143 8.3895 66.5243 8.5305C66.4373 8.6715 66.3743 8.829 66.3353 9.003C66.2963 9.174 66.2768 9.3555 66.2768 9.5475V12H65.3273ZM70.8303 12.135C70.3443 12.135 69.9228 12.0255 69.5658 11.8065C69.2088 11.5875 68.9328 11.286 68.7378 10.902C68.5458 10.515 68.4498 10.0695 68.4498 9.5655C68.4498 9.0585 68.5488 8.613 68.7468 8.229C68.9448 7.842 69.2223 7.542 69.5793 7.329C69.9363 7.113 70.3533 7.005 70.8303 7.005C71.3163 7.005 71.7378 7.1145 72.0948 7.3335C72.4518 7.5525 72.7278 7.854 72.9228 8.238C73.1178 8.622 73.2153 9.0645 73.2153 9.5655C73.2153 10.0725 73.1163 10.5195 72.9183 10.9065C72.7233 11.2905 72.4473 11.592 72.0903 11.811C71.7333 12.027 71.3133 12.135 70.8303 12.135ZM70.8303 11.2485C71.2953 11.2485 71.6418 11.0925 71.8698 10.7805C72.1008 10.4655 72.2163 10.0605 72.2163 9.5655C72.2163 9.0585 72.0993 8.6535 71.8653 8.3505C71.6343 8.0445 71.2893 7.8915 70.8303 7.8915C70.5153 7.8915 70.2558 7.9635 70.0518 8.1075C69.8478 8.2485 69.6963 8.445 69.5973 8.697C69.4983 8.946 69.4488 9.2355 69.4488 9.5655C69.4488 10.0755 69.5658 10.4835 69.7998 10.7895C70.0338 11.0955 70.3773 11.2485 70.8303 11.2485ZM75.3424 12L73.5784 7.14H74.5279L75.8194 10.821L77.1109 7.14H78.0604L76.2964 12H75.3424ZM80.8509 12.135C80.3679 12.135 79.9434 12.03 79.5774 11.82C79.2144 11.607 78.9309 11.3115 78.7269 10.9335C78.5259 10.5525 78.4254 10.1115 78.4254 9.6105C78.4254 9.0795 78.5244 8.619 78.7224 8.229C78.9234 7.839 79.2024 7.5375 79.5594 7.3245C79.9164 7.1115 80.3319 7.005 80.8059 7.005C81.3009 7.005 81.7224 7.1205 82.0704 7.3515C82.4184 7.5795 82.6779 7.905 82.8489 8.328C83.0229 8.751 83.0919 9.2535 83.0559 9.8355H82.1154V9.4935C82.1094 8.9295 82.0014 8.5125 81.7914 8.2425C81.5844 7.9725 81.2679 7.8375 80.8419 7.8375C80.3709 7.8375 80.0169 7.986 79.7799 8.283C79.5429 8.58 79.4244 9.009 79.4244 9.57C79.4244 10.104 79.5429 10.518 79.7799 10.812C80.0169 11.103 80.3589 11.2485 80.8059 11.2485C81.0999 11.2485 81.3534 11.1825 81.5664 11.0505C81.7824 10.9155 81.9504 10.7235 82.0704 10.4745L82.9929 10.767C82.8039 11.202 82.5174 11.5395 82.1334 11.7795C81.7494 12.0165 81.3219 12.135 80.8509 12.135ZM79.1184 9.8355V9.102H82.5879V9.8355H79.1184ZM85.996 12.135C85.543 12.135 85.1515 12.0225 84.8215 11.7975C84.4945 11.5725 84.241 11.2665 84.061 10.8795C83.884 10.4895 83.7955 10.0515 83.7955 9.5655C83.7955 9.0765 83.8855 8.64 84.0655 8.256C84.2455 7.869 84.5005 7.5645 84.8305 7.3425C85.1635 7.1175 85.558 7.005 86.014 7.005C86.473 7.005 86.8585 7.1175 87.1705 7.3425C87.4855 7.5645 87.7225 7.869 87.8815 8.256C88.0435 8.643 88.1245 9.0795 88.1245 9.5655C88.1245 10.0515 88.0435 10.488 87.8815 10.875C87.7195 11.262 87.481 11.5695 87.166 11.7975C86.851 12.0225 86.461 12.135 85.996 12.135ZM86.1265 11.2845C86.4355 11.2845 86.6875 11.211 86.8825 11.064C87.0775 10.917 87.22 10.7145 87.31 10.4565C87.4 10.1985 87.445 9.9015 87.445 9.5655C87.445 9.2295 87.3985 8.9325 87.3055 8.6745C87.2155 8.4165 87.0745 8.2155 86.8825 8.0715C86.6935 7.9275 86.452 7.8555 86.158 7.8555C85.846 7.8555 85.5895 7.932 85.3885 8.085C85.1875 8.238 85.0375 8.4435 84.9385 8.7015C84.8425 8.9595 84.7945 9.2475 84.7945 9.5655C84.7945 9.8865 84.8425 10.1775 84.9385 10.4385C85.0375 10.6965 85.1845 10.902 85.3795 11.055C85.5775 11.208 85.8265 11.2845 86.1265 11.2845ZM87.445 12V8.5035H87.337V5.52H88.282V12H87.445Z"
                        fill={theme.colors["white"]}
                      />
                    </DisapprovedMutitestRowBadge>
                  </div>
                )}
              </>
            ) : (
              <TdInner>
                {[
                  OptimisationTestStatusEnum.PROCESSING,
                  OptimisationTestStatusEnum.WAITING_FOR_DATA,
                ].includes(test.status) && (
                  <StatusIndicator
                    $status={test.hasError ? -100 : test.status}
                  />
                )}
                {tableViewOptions["Thumb"] && !hideThumbnail && (
                  <ThumbHolder>
                    <ScalableThumbnail
                      areThumbsEnlarged={areThumbsEnlarged}
                      setAreThumbsEnlarged={setAreThumbsEnlarged}
                      thumbnail={test.thumbnail}
                    />
                    {test.multitestEnabled && !isMultitestRow && (
                      <MultitestButton
                        type="button"
                        onClick={onMultitestBtnClick}
                      >
                        {hasDisapprovedAd && (
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              cx="5"
                              cy="5"
                              r="5"
                              fill={theme.colors["error"]}
                            />
                            <path
                              d="M4.53125 3.60662V2.8125H5.53125V3.60662H4.53125ZM4.53125 7.3125V4.13603H5.53125V7.3125H4.53125Z"
                              fill={theme.colors["white"]}
                            />
                          </svg>
                        )}
                        <Text as="span" $type="extraSmall">
                          Multitest
                        </Text>
                        <MultitestButtonArrow
                          $rotate={showMultitest}
                          width="8"
                          height="6"
                          viewBox="0 0 8 6"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M1 1.5L4 4.5L7 1.5" fill="transparent" />
                          <path
                            d="M1 1.5L4 4.5L7 1.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </MultitestButtonArrow>
                      </MultitestButton>
                    )}
                  </ThumbHolder>
                )}
                <TextWraper>
                  {test.youtubeVideoUrl ? (
                    <a
                      target="_blank"
                      href={test.youtubeVideoUrl}
                      rel="noreferrer"
                    >
                      {test.name} - T{test.batch}
                    </a>
                  ) : (
                    <>
                      {test.name} - T{test.batch}
                    </>
                  )}
                  {test.isDuplicate && (
                    <DuplicateText $type="smallBold" $color="gray" as="span">
                      {" "}
                      (2)
                      <br />
                      <DuplicateIcon />
                    </DuplicateText>
                  )}
                  {!!test.author && (
                    <Author as="div" $type="extraSmall" $color="gray">
                      {test.author}
                    </Author>
                  )}
                  <BadgesHolder>
                    {test.type === OptimisationTestTypeEnum.THUMBS &&
                      test.cBatch !== 0 && (
                        <TestCompetitionBadge type={test.cBatch} />
                      )}
                    {!test.multitestEnabled && hasDisapprovedAd && (
                      <svg
                        width="97"
                        height="18"
                        viewBox="0 0 97 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          width="97"
                          height="18"
                          rx="9"
                          fill={theme.colors["error"]}
                        />
                        <circle
                          cx="13"
                          cy="9"
                          r="5"
                          fill={theme.colors["white"]}
                        />
                        <path
                          d="M12.5312 7.60662V6.8125H13.5312V7.60662H12.5312ZM12.5312 11.3125V8.13603H13.5312V11.3125H12.5312Z"
                          fill={theme.colors["error"]}
                        />
                        <path
                          d="M22.18 12L24.286 5.52H25.6405L27.7465 12H26.77L24.8305 6.087H25.0735L23.1565 12H22.18ZM23.2735 10.5375V9.6555H26.6575V10.5375H23.2735ZM30.4843 12.135C30.0313 12.135 29.6398 12.0225 29.3098 11.7975C28.9828 11.5725 28.7293 11.2665 28.5493 10.8795C28.3723 10.4895 28.2838 10.0515 28.2838 9.5655C28.2838 9.0765 28.3738 8.64 28.5538 8.256C28.7338 7.869 28.9888 7.5645 29.3188 7.3425C29.6518 7.1175 30.0463 7.005 30.5023 7.005C30.9613 7.005 31.3468 7.1175 31.6588 7.3425C31.9738 7.5645 32.2108 7.869 32.3698 8.256C32.5318 8.643 32.6128 9.0795 32.6128 9.5655C32.6128 10.0515 32.5318 10.488 32.3698 10.875C32.2078 11.262 31.9693 11.5695 31.6543 11.7975C31.3393 12.0225 30.9493 12.135 30.4843 12.135ZM30.6148 11.2845C30.9238 11.2845 31.1758 11.211 31.3708 11.064C31.5658 10.917 31.7083 10.7145 31.7983 10.4565C31.8883 10.1985 31.9333 9.9015 31.9333 9.5655C31.9333 9.2295 31.8868 8.9325 31.7938 8.6745C31.7038 8.4165 31.5628 8.2155 31.3708 8.0715C31.1818 7.9275 30.9403 7.8555 30.6463 7.8555C30.3343 7.8555 30.0778 7.932 29.8768 8.085C29.6758 8.238 29.5258 8.4435 29.4268 8.7015C29.3308 8.9595 29.2828 9.2475 29.2828 9.5655C29.2828 9.8865 29.3308 10.1775 29.4268 10.4385C29.5258 10.6965 29.6728 10.902 29.8678 11.055C30.0658 11.208 30.3148 11.2845 30.6148 11.2845ZM31.9333 12V8.5035H31.8253V5.52H32.7703V12H31.9333ZM35.8312 12V5.52H37.8607C37.9207 5.52 38.0317 5.5215 38.1937 5.5245C38.3587 5.5275 38.5162 5.5395 38.6662 5.5605C39.1732 5.6235 39.5992 5.805 39.9442 6.105C40.2922 6.405 40.5547 6.786 40.7317 7.248C40.9087 7.707 40.9972 8.211 40.9972 8.76C40.9972 9.312 40.9087 9.819 40.7317 10.281C40.5547 10.74 40.2922 11.1195 39.9442 11.4195C39.5992 11.7165 39.1732 11.8965 38.6662 11.9595C38.5162 11.9805 38.3587 11.9925 38.1937 11.9955C38.0317 11.9985 37.9207 12 37.8607 12H35.8312ZM36.7942 11.1045H37.8607C37.9627 11.1045 38.0842 11.1015 38.2252 11.0955C38.3662 11.0895 38.4907 11.0775 38.5987 11.0595C38.9287 10.9965 39.1957 10.854 39.3997 10.632C39.6067 10.407 39.7582 10.131 39.8542 9.804C39.9502 9.477 39.9982 9.129 39.9982 8.76C39.9982 8.379 39.9487 8.0265 39.8497 7.7025C39.7507 7.3755 39.5977 7.1025 39.3907 6.8835C39.1867 6.6615 38.9227 6.5205 38.5987 6.4605C38.4907 6.4395 38.3647 6.4275 38.2207 6.4245C38.0797 6.4185 37.9597 6.4155 37.8607 6.4155H36.7942V11.1045ZM42.135 6.3435V5.43H43.0755V6.3435H42.135ZM42.135 12V7.14H43.0755V12H42.135ZM46.2854 12.1305C45.6944 12.1305 45.2129 12 44.8409 11.739C44.4689 11.478 44.2409 11.1105 44.1569 10.6365L45.1199 10.488C45.1799 10.74 45.3179 10.9395 45.5339 11.0865C45.7529 11.2305 46.0244 11.3025 46.3484 11.3025C46.6424 11.3025 46.8719 11.2425 47.0369 11.1225C47.2049 11.0025 47.2889 10.8375 47.2889 10.6275C47.2889 10.5045 47.2589 10.4055 47.1989 10.3305C47.1419 10.2525 47.0204 10.179 46.8344 10.11C46.6484 10.041 46.3649 9.9555 45.9839 9.8535C45.5669 9.7455 45.2354 9.63 44.9894 9.507C44.7464 9.381 44.5724 9.2355 44.4674 9.0705C44.3654 8.9025 44.3144 8.7 44.3144 8.463C44.3144 8.169 44.3924 7.9125 44.5484 7.6935C44.7044 7.4745 44.9234 7.305 45.2054 7.185C45.4904 7.065 45.8234 7.005 46.2044 7.005C46.5764 7.005 46.9079 7.0635 47.1989 7.1805C47.4899 7.2975 47.7254 7.464 47.9054 7.68C48.0854 7.893 48.1934 8.1435 48.2294 8.4315L47.2664 8.607C47.2334 8.373 47.1239 8.1885 46.9379 8.0535C46.7519 7.9185 46.5104 7.845 46.2134 7.833C45.9284 7.821 45.6974 7.869 45.5204 7.977C45.3434 8.082 45.2549 8.2275 45.2549 8.4135C45.2549 8.5215 45.2879 8.613 45.3539 8.688C45.4229 8.763 45.5549 8.835 45.7499 8.904C45.9449 8.973 46.2329 9.0555 46.6139 9.1515C47.0219 9.2565 47.3459 9.3735 47.5859 9.5025C47.8259 9.6285 47.9969 9.78 48.0989 9.957C48.2039 10.131 48.2564 10.3425 48.2564 10.5915C48.2564 11.0715 48.0809 11.448 47.7299 11.721C47.3819 11.994 46.9004 12.1305 46.2854 12.1305ZM50.6246 12.135C50.2646 12.135 49.9631 12.069 49.7201 11.937C49.4771 11.802 49.2926 11.625 49.1666 11.406C49.0436 11.184 48.9821 10.941 48.9821 10.677C48.9821 10.431 49.0256 10.215 49.1126 10.029C49.1996 9.843 49.3286 9.6855 49.4996 9.5565C49.6706 9.4245 49.8806 9.318 50.1296 9.237C50.3456 9.174 50.5901 9.1185 50.8631 9.0705C51.1361 9.0225 51.4226 8.9775 51.7226 8.9355C52.0256 8.8935 52.3256 8.8515 52.6226 8.8095L52.2806 8.9985C52.2866 8.6175 52.2056 8.3355 52.0376 8.1525C51.8726 7.9665 51.5876 7.8735 51.1826 7.8735C50.9276 7.8735 50.6936 7.9335 50.4806 8.0535C50.2676 8.1705 50.1191 8.3655 50.0351 8.6385L49.1576 8.3685C49.2776 7.9515 49.5056 7.62 49.8416 7.374C50.1806 7.128 50.6306 7.005 51.1916 7.005C51.6266 7.005 52.0046 7.08 52.3256 7.23C52.6496 7.377 52.8866 7.611 53.0366 7.932C53.1146 8.091 53.1626 8.259 53.1806 8.436C53.1986 8.613 53.2076 8.8035 53.2076 9.0075V12H52.3751V10.8885L52.5371 11.0325C52.3361 11.4045 52.0796 11.682 51.7676 11.865C51.4586 12.045 51.0776 12.135 50.6246 12.135ZM50.7911 11.3655C51.0581 11.3655 51.2876 11.319 51.4796 11.226C51.6716 11.13 51.8261 11.0085 51.9431 10.8615C52.0601 10.7145 52.1366 10.5615 52.1726 10.4025C52.2236 10.2585 52.2521 10.0965 52.2581 9.9165C52.2671 9.7365 52.2716 9.5925 52.2716 9.4845L52.5776 9.597C52.2806 9.642 52.0106 9.6825 51.7676 9.7185C51.5246 9.7545 51.3041 9.7905 51.1061 9.8265C50.9111 9.8595 50.7371 9.9 50.5841 9.948C50.4551 9.993 50.3396 10.047 50.2376 10.11C50.1386 10.173 50.0591 10.2495 49.9991 10.3395C49.9421 10.4295 49.9136 10.539 49.9136 10.668C49.9136 10.794 49.9451 10.911 50.0081 11.019C50.0711 11.124 50.1671 11.208 50.2961 11.271C50.4251 11.334 50.5901 11.3655 50.7911 11.3655ZM56.6576 12.135C56.1926 12.135 55.8026 12.0225 55.4876 11.7975C55.1726 11.5695 54.9341 11.262 54.7721 10.875C54.6101 10.488 54.5291 10.0515 54.5291 9.5655C54.5291 9.0795 54.6086 8.643 54.7676 8.256C54.9296 7.869 55.1666 7.5645 55.4786 7.3425C55.7936 7.1175 56.1806 7.005 56.6396 7.005C57.0956 7.005 57.4886 7.1175 57.8186 7.3425C58.1516 7.5645 58.4081 7.869 58.5881 8.256C58.7681 8.64 58.8581 9.0765 58.8581 9.5655C58.8581 10.0515 58.7681 10.4895 58.5881 10.8795C58.4111 11.2665 58.1576 11.5725 57.8276 11.7975C57.5006 12.0225 57.1106 12.135 56.6576 12.135ZM54.3716 14.16V7.14H55.2086V10.6365H55.3166V14.16H54.3716ZM56.5271 11.2845C56.8271 11.2845 57.0746 11.208 57.2696 11.055C57.4676 10.902 57.6146 10.6965 57.7106 10.4385C57.8096 10.1775 57.8591 9.8865 57.8591 9.5655C57.8591 9.2475 57.8096 8.9595 57.7106 8.7015C57.6146 8.4435 57.4661 8.238 57.2651 8.085C57.0641 7.932 56.8076 7.8555 56.4956 7.8555C56.2016 7.8555 55.9586 7.9275 55.7666 8.0715C55.5776 8.2155 55.4366 8.4165 55.3436 8.6745C55.2536 8.9325 55.2086 9.2295 55.2086 9.5655C55.2086 9.9015 55.2536 10.1985 55.3436 10.4565C55.4336 10.7145 55.5761 10.917 55.7711 11.064C55.9661 11.211 56.2181 11.2845 56.5271 11.2845ZM62.1332 12.135C61.6682 12.135 61.2782 12.0225 60.9632 11.7975C60.6482 11.5695 60.4097 11.262 60.2477 10.875C60.0857 10.488 60.0047 10.0515 60.0047 9.5655C60.0047 9.0795 60.0842 8.643 60.2432 8.256C60.4052 7.869 60.6422 7.5645 60.9542 7.3425C61.2692 7.1175 61.6562 7.005 62.1152 7.005C62.5712 7.005 62.9642 7.1175 63.2942 7.3425C63.6272 7.5645 63.8837 7.869 64.0637 8.256C64.2437 8.64 64.3337 9.0765 64.3337 9.5655C64.3337 10.0515 64.2437 10.4895 64.0637 10.8795C63.8867 11.2665 63.6332 11.5725 63.3032 11.7975C62.9762 12.0225 62.5862 12.135 62.1332 12.135ZM59.8472 14.16V7.14H60.6842V10.6365H60.7922V14.16H59.8472ZM62.0027 11.2845C62.3027 11.2845 62.5502 11.208 62.7452 11.055C62.9432 10.902 63.0902 10.6965 63.1862 10.4385C63.2852 10.1775 63.3347 9.8865 63.3347 9.5655C63.3347 9.2475 63.2852 8.9595 63.1862 8.7015C63.0902 8.4435 62.9417 8.238 62.7407 8.085C62.5397 7.932 62.2832 7.8555 61.9712 7.8555C61.6772 7.8555 61.4342 7.9275 61.2422 8.0715C61.0532 8.2155 60.9122 8.4165 60.8192 8.6745C60.7292 8.9325 60.6842 9.2295 60.6842 9.5655C60.6842 9.9015 60.7292 10.1985 60.8192 10.4565C60.9092 10.7145 61.0517 10.917 61.2467 11.064C61.4417 11.211 61.6937 11.2845 62.0027 11.2845ZM65.3273 12V7.14H66.1643V8.319L66.0473 8.166C66.1073 8.01 66.1853 7.8675 66.2813 7.7385C66.3773 7.6065 66.4883 7.4985 66.6143 7.4145C66.7373 7.3245 66.8738 7.2555 67.0238 7.2075C67.1768 7.1565 67.3328 7.1265 67.4918 7.1175C67.6508 7.1055 67.8038 7.113 67.9508 7.14V8.022C67.7918 7.98 67.6148 7.968 67.4198 7.986C67.2278 8.004 67.0508 8.0655 66.8888 8.1705C66.7358 8.2695 66.6143 8.3895 66.5243 8.5305C66.4373 8.6715 66.3743 8.829 66.3353 9.003C66.2963 9.174 66.2768 9.3555 66.2768 9.5475V12H65.3273ZM70.8303 12.135C70.3443 12.135 69.9228 12.0255 69.5658 11.8065C69.2088 11.5875 68.9328 11.286 68.7378 10.902C68.5458 10.515 68.4498 10.0695 68.4498 9.5655C68.4498 9.0585 68.5488 8.613 68.7468 8.229C68.9448 7.842 69.2223 7.542 69.5793 7.329C69.9363 7.113 70.3533 7.005 70.8303 7.005C71.3163 7.005 71.7378 7.1145 72.0948 7.3335C72.4518 7.5525 72.7278 7.854 72.9228 8.238C73.1178 8.622 73.2153 9.0645 73.2153 9.5655C73.2153 10.0725 73.1163 10.5195 72.9183 10.9065C72.7233 11.2905 72.4473 11.592 72.0903 11.811C71.7333 12.027 71.3133 12.135 70.8303 12.135ZM70.8303 11.2485C71.2953 11.2485 71.6418 11.0925 71.8698 10.7805C72.1008 10.4655 72.2163 10.0605 72.2163 9.5655C72.2163 9.0585 72.0993 8.6535 71.8653 8.3505C71.6343 8.0445 71.2893 7.8915 70.8303 7.8915C70.5153 7.8915 70.2558 7.9635 70.0518 8.1075C69.8478 8.2485 69.6963 8.445 69.5973 8.697C69.4983 8.946 69.4488 9.2355 69.4488 9.5655C69.4488 10.0755 69.5658 10.4835 69.7998 10.7895C70.0338 11.0955 70.3773 11.2485 70.8303 11.2485ZM75.3424 12L73.5784 7.14H74.5279L75.8194 10.821L77.1109 7.14H78.0604L76.2964 12H75.3424ZM80.8509 12.135C80.3679 12.135 79.9434 12.03 79.5774 11.82C79.2144 11.607 78.9309 11.3115 78.7269 10.9335C78.5259 10.5525 78.4254 10.1115 78.4254 9.6105C78.4254 9.0795 78.5244 8.619 78.7224 8.229C78.9234 7.839 79.2024 7.5375 79.5594 7.3245C79.9164 7.1115 80.3319 7.005 80.8059 7.005C81.3009 7.005 81.7224 7.1205 82.0704 7.3515C82.4184 7.5795 82.6779 7.905 82.8489 8.328C83.0229 8.751 83.0919 9.2535 83.0559 9.8355H82.1154V9.4935C82.1094 8.9295 82.0014 8.5125 81.7914 8.2425C81.5844 7.9725 81.2679 7.8375 80.8419 7.8375C80.3709 7.8375 80.0169 7.986 79.7799 8.283C79.5429 8.58 79.4244 9.009 79.4244 9.57C79.4244 10.104 79.5429 10.518 79.7799 10.812C80.0169 11.103 80.3589 11.2485 80.8059 11.2485C81.0999 11.2485 81.3534 11.1825 81.5664 11.0505C81.7824 10.9155 81.9504 10.7235 82.0704 10.4745L82.9929 10.767C82.8039 11.202 82.5174 11.5395 82.1334 11.7795C81.7494 12.0165 81.3219 12.135 80.8509 12.135ZM79.1184 9.8355V9.102H82.5879V9.8355H79.1184ZM85.996 12.135C85.543 12.135 85.1515 12.0225 84.8215 11.7975C84.4945 11.5725 84.241 11.2665 84.061 10.8795C83.884 10.4895 83.7955 10.0515 83.7955 9.5655C83.7955 9.0765 83.8855 8.64 84.0655 8.256C84.2455 7.869 84.5005 7.5645 84.8305 7.3425C85.1635 7.1175 85.558 7.005 86.014 7.005C86.473 7.005 86.8585 7.1175 87.1705 7.3425C87.4855 7.5645 87.7225 7.869 87.8815 8.256C88.0435 8.643 88.1245 9.0795 88.1245 9.5655C88.1245 10.0515 88.0435 10.488 87.8815 10.875C87.7195 11.262 87.481 11.5695 87.166 11.7975C86.851 12.0225 86.461 12.135 85.996 12.135ZM86.1265 11.2845C86.4355 11.2845 86.6875 11.211 86.8825 11.064C87.0775 10.917 87.22 10.7145 87.31 10.4565C87.4 10.1985 87.445 9.9015 87.445 9.5655C87.445 9.2295 87.3985 8.9325 87.3055 8.6745C87.2155 8.4165 87.0745 8.2155 86.8825 8.0715C86.6935 7.9275 86.452 7.8555 86.158 7.8555C85.846 7.8555 85.5895 7.932 85.3885 8.085C85.1875 8.238 85.0375 8.4435 84.9385 8.7015C84.8425 8.9595 84.7945 9.2475 84.7945 9.5655C84.7945 9.8865 84.8425 10.1775 84.9385 10.4385C85.0375 10.6965 85.1845 10.902 85.3795 11.055C85.5775 11.208 85.8265 11.2845 86.1265 11.2845ZM87.445 12V8.5035H87.337V5.52H88.282V12H87.445Z"
                          fill={theme.colors["white"]}
                        />
                      </svg>
                    )}
                    {showPartialBadge && (
                      <svg
                        width="58"
                        height="18"
                        viewBox="0 0 58 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          width="58"
                          height="18"
                          rx="9"
                          fill={theme.colors.warning}
                        />
                        <circle cx="13" cy="9" r="5" fill="white" />
                        <path
                          d="M12.5312 7.60662V6.8125H13.5312V7.60662H12.5312ZM12.5312 11.3125V8.13603H13.5312V11.3125H12.5312Z"
                          fill={theme.colors.warning}
                        />
                        <path
                          d="M22.63 12V5.52H25.249C25.312 5.52 25.39 5.523 25.483 5.529C25.576 5.532 25.6645 5.541 25.7485 5.556C26.1085 5.613 26.4085 5.736 26.6485 5.925C26.8915 6.114 27.073 6.3525 27.193 6.6405C27.313 6.9285 27.373 7.2465 27.373 7.5945C27.373 7.9455 27.313 8.265 27.193 8.553C27.073 8.841 26.8915 9.0795 26.6485 9.2685C26.4085 9.4575 26.1085 9.5805 25.7485 9.6375C25.6645 9.6495 25.5745 9.6585 25.4785 9.6645C25.3855 9.6705 25.309 9.6735 25.249 9.6735H23.5705V12H22.63ZM23.5705 8.7825H25.213C25.273 8.7825 25.339 8.7795 25.411 8.7735C25.486 8.7675 25.5565 8.757 25.6225 8.742C25.8145 8.697 25.969 8.6145 26.086 8.4945C26.203 8.3715 26.287 8.2305 26.338 8.0715C26.389 7.9125 26.4145 7.7535 26.4145 7.5945C26.4145 7.4355 26.389 7.278 26.338 7.122C26.287 6.963 26.203 6.8235 26.086 6.7035C25.969 6.5805 25.8145 6.4965 25.6225 6.4515C25.5565 6.4335 25.486 6.4215 25.411 6.4155C25.339 6.4095 25.273 6.4065 25.213 6.4065H23.5705V8.7825ZM29.4957 12.135C29.1357 12.135 28.8342 12.069 28.5912 11.937C28.3482 11.802 28.1637 11.625 28.0377 11.406C27.9147 11.184 27.8532 10.941 27.8532 10.677C27.8532 10.431 27.8967 10.215 27.9837 10.029C28.0707 9.843 28.1997 9.6855 28.3707 9.5565C28.5417 9.4245 28.7517 9.318 29.0007 9.237C29.2167 9.174 29.4612 9.1185 29.7342 9.0705C30.0072 9.0225 30.2937 8.9775 30.5937 8.9355C30.8967 8.8935 31.1967 8.8515 31.4937 8.8095L31.1517 8.9985C31.1577 8.6175 31.0767 8.3355 30.9087 8.1525C30.7437 7.9665 30.4587 7.8735 30.0537 7.8735C29.7987 7.8735 29.5647 7.9335 29.3517 8.0535C29.1387 8.1705 28.9902 8.3655 28.9062 8.6385L28.0287 8.3685C28.1487 7.9515 28.3767 7.62 28.7127 7.374C29.0517 7.128 29.5017 7.005 30.0627 7.005C30.4977 7.005 30.8757 7.08 31.1967 7.23C31.5207 7.377 31.7577 7.611 31.9077 7.932C31.9857 8.091 32.0337 8.259 32.0517 8.436C32.0697 8.613 32.0787 8.8035 32.0787 9.0075V12H31.2462V10.8885L31.4082 11.0325C31.2072 11.4045 30.9507 11.682 30.6387 11.865C30.3297 12.045 29.9487 12.135 29.4957 12.135ZM29.6622 11.3655C29.9292 11.3655 30.1587 11.319 30.3507 11.226C30.5427 11.13 30.6972 11.0085 30.8142 10.8615C30.9312 10.7145 31.0077 10.5615 31.0437 10.4025C31.0947 10.2585 31.1232 10.0965 31.1292 9.9165C31.1382 9.7365 31.1427 9.5925 31.1427 9.4845L31.4487 9.597C31.1517 9.642 30.8817 9.6825 30.6387 9.7185C30.3957 9.7545 30.1752 9.7905 29.9772 9.8265C29.7822 9.8595 29.6082 9.9 29.4552 9.948C29.3262 9.993 29.2107 10.047 29.1087 10.11C29.0097 10.173 28.9302 10.2495 28.8702 10.3395C28.8132 10.4295 28.7847 10.539 28.7847 10.668C28.7847 10.794 28.8162 10.911 28.8792 11.019C28.9422 11.124 29.0382 11.208 29.1672 11.271C29.2962 11.334 29.4612 11.3655 29.6622 11.3655ZM33.2472 12V7.14H34.0842V8.319L33.9672 8.166C34.0272 8.01 34.1052 7.8675 34.2012 7.7385C34.2972 7.6065 34.4082 7.4985 34.5342 7.4145C34.6572 7.3245 34.7937 7.2555 34.9437 7.2075C35.0967 7.1565 35.2527 7.1265 35.4117 7.1175C35.5707 7.1055 35.7237 7.113 35.8707 7.14V8.022C35.7117 7.98 35.5347 7.968 35.3397 7.986C35.1477 8.004 34.9707 8.0655 34.8087 8.1705C34.6557 8.2695 34.5342 8.3895 34.4442 8.5305C34.3572 8.6715 34.2942 8.829 34.2552 9.003C34.2162 9.174 34.1967 9.3555 34.1967 9.5475V12H33.2472ZM39.5087 12C39.2027 12.06 38.9027 12.0855 38.6087 12.0765C38.3147 12.0675 38.0522 12.0105 37.8212 11.9055C37.5902 11.8005 37.4162 11.6355 37.2992 11.4105C37.1942 11.2095 37.1372 11.0055 37.1282 10.7985C37.1222 10.5885 37.1192 10.3515 37.1192 10.0875V5.79H38.0642V10.0425C38.0642 10.2375 38.0657 10.407 38.0687 10.551C38.0747 10.695 38.1062 10.8165 38.1632 10.9155C38.2712 11.1015 38.4422 11.208 38.6762 11.235C38.9132 11.259 39.1907 11.2485 39.5087 11.2035V12ZM36.1877 7.896V7.14H39.5087V7.896H36.1877ZM40.6233 6.3435V5.43H41.5638V6.3435H40.6233ZM40.6233 12V7.14H41.5638V12H40.6233ZM44.2877 12.135C43.9277 12.135 43.6262 12.069 43.3832 11.937C43.1402 11.802 42.9557 11.625 42.8297 11.406C42.7067 11.184 42.6452 10.941 42.6452 10.677C42.6452 10.431 42.6887 10.215 42.7757 10.029C42.8627 9.843 42.9917 9.6855 43.1627 9.5565C43.3337 9.4245 43.5437 9.318 43.7927 9.237C44.0087 9.174 44.2532 9.1185 44.5262 9.0705C44.7992 9.0225 45.0857 8.9775 45.3857 8.9355C45.6887 8.8935 45.9887 8.8515 46.2857 8.8095L45.9437 8.9985C45.9497 8.6175 45.8687 8.3355 45.7007 8.1525C45.5357 7.9665 45.2507 7.8735 44.8457 7.8735C44.5907 7.8735 44.3567 7.9335 44.1437 8.0535C43.9307 8.1705 43.7822 8.3655 43.6982 8.6385L42.8207 8.3685C42.9407 7.9515 43.1687 7.62 43.5047 7.374C43.8437 7.128 44.2937 7.005 44.8547 7.005C45.2897 7.005 45.6677 7.08 45.9887 7.23C46.3127 7.377 46.5497 7.611 46.6997 7.932C46.7777 8.091 46.8257 8.259 46.8437 8.436C46.8617 8.613 46.8707 8.8035 46.8707 9.0075V12H46.0382V10.8885L46.2002 11.0325C45.9992 11.4045 45.7427 11.682 45.4307 11.865C45.1217 12.045 44.7407 12.135 44.2877 12.135ZM44.4542 11.3655C44.7212 11.3655 44.9507 11.319 45.1427 11.226C45.3347 11.13 45.4892 11.0085 45.6062 10.8615C45.7232 10.7145 45.7997 10.5615 45.8357 10.4025C45.8867 10.2585 45.9152 10.0965 45.9212 9.9165C45.9302 9.7365 45.9347 9.5925 45.9347 9.4845L46.2407 9.597C45.9437 9.642 45.6737 9.6825 45.4307 9.7185C45.1877 9.7545 44.9672 9.7905 44.7692 9.8265C44.5742 9.8595 44.4002 9.9 44.2472 9.948C44.1182 9.993 44.0027 10.047 43.9007 10.11C43.8017 10.173 43.7222 10.2495 43.6622 10.3395C43.6052 10.4295 43.5767 10.539 43.5767 10.668C43.5767 10.794 43.6082 10.911 43.6712 11.019C43.7342 11.124 43.8302 11.208 43.9592 11.271C44.0882 11.334 44.2532 11.3655 44.4542 11.3655ZM48.1292 12V5.385H49.0697V12H48.1292Z"
                          fill={theme.colors.white}
                        />
                      </svg>
                    )}
                    {!!winning && (
                      <Winning $type="extraSmall" $color="white" as="div">
                        <RocketIcon />
                        Winning
                      </Winning>
                    )}
                  </BadgesHolder>
                </TextWraper>
              </TdInner>
            )}
          </StyledTd>
          {renderMetricsColumns()}
          {renderColumns()}
          {[
            OptimisationTestTypeEnum.SEQUENCES,
            OptimisationTestTypeEnum.TIPS,
          ].includes(test.type) && (
            <>
              {tableViewOptions["Drop Visualization"] && (
                <GraphTd>
                  {!!test.retention ? (
                    <RetentionGraph data={test.retention} />
                  ) : (
                    <RetentionGraphPlaceholder />
                  )}
                </GraphTd>
              )}
              {tableViewOptions["Drop"] && (
                <Td>
                  {!!dropPoints?.length ? (
                    dropPoints.map(
                      (
                        dropPoint: {
                          current: OptimisationRetentionResource;
                          previous: OptimisationRetentionResource;
                        },
                        index: number
                      ) => (
                        <DropText key={index}>
                          {Math.round(
                            dropPoint.current.elapsed_video_time_ratio *
                              test.videoDuration
                          )}{" "}
                          s:{" "}
                          <span>
                            {(
                              (dropPoint.previous.audience_watch_ratio -
                                dropPoint.current.audience_watch_ratio) *
                              100
                            ).toFixed(2)}
                            %
                          </span>
                        </DropText>
                      )
                    )
                  ) : (
                    <>
                      <DropText>
                        - s: <span> - %</span>
                      </DropText>
                      <DropText>
                        - s: <span> - %</span>
                      </DropText>
                      <DropText>
                        - s: <span> - %</span>
                      </DropText>
                    </>
                  )}
                </Td>
              )}
            </>
          )}
          <LastTd>{renderActions()}</LastTd>
        </Tr>
        {showMultitest &&
          test[
            gaNetworkType === GaNetworkType.SEARCH ? "statsS" : "statsW"
          ]?.map((stats, index) => (
            <TestTableRow
              winning={false}
              isDragging={false}
              isDraggable={false}
              areThumbsEnlarged={areThumbsEnlarged}
              setAreThumbsEnlarged={setAreThumbsEnlarged}
              key={index}
              isMultitestRow
              isLastRow={
                index + 1 ===
                test[
                  gaNetworkType === GaNetworkType.SEARCH ? "statsS" : "statsW"
                ]?.length
              }
              test={{
                ...test,
                youtubeVideoUrl: stats.youtubeVideoUrl,
                watchStats: stats,
                searchStats: stats,
              }}
            />
          ))}
      </>
    );
  }
);

export default TestTableRow;
